import { html } from 'lit-element';

import sanitizeHTML from '@elab/core/utils/sanitizeHTML';
import connect from '@elab/core/store/connect';
import { selectors as checkoutSelectors } from '@elab/core/store/checkout';
import getSelectors from '@elab/core/utils/dynaForms/selectors';
import animationHelpers from '@elab/core/utils/ui/animationHelpers';
import { cacheSelector } from '@elab/core/utils/webComponent';

import { FormToggler } from './FormToggler';
import { FormField } from './FormField';

export class FormMultifield extends FormField {
    static get properties() {
        return {
            addLabel: { type: String, attribute: 'add-label' },
            removeLabel: { type: String, attribute: 'remove-label' },
            slotContent: { type: String },
            maxItems: { type: Number, attribute: 'max-items' },
            narrow: { type: Boolean, attribute: 'narrow' },
        };
    }

    constructor() {
        super();
        this.addLabel = 'add';
        this.removeLabel = 'remove';
        this.maxItems = 0;
        this.narrow = false;
    }

    addItem(evt) {
        evt.preventDefault();
        this.dispatchEvent(
            new CustomEvent('ADD_FIELD_ITEM', {
                detail: { storeName: this.storeName, name: this.name },
            }),
        );
        return false;
    }

    removeItem(evt, index) {
        evt.preventDefault();
        this.dispatchEvent(
            new CustomEvent('REMOVE_FIELD_ITEM', {
                detail: { storeName: this.storeName, name: this.name, index },
            }),
        );
        return null;
    }

    renderFieldData(index) {
        return html`
            <e2-form-field
                class="form-field ${this.narrow ? 'form-field--narrow' : ''}"
                store-name="${this.storeName}"
                name="${this.name}.value.${index}"
            >
                ${html([sanitizeHTML(this.slotContent)])}
            </e2-form-field>
            ${this.fieldValue.value.length > 1 ? this.renderRemoveButton(index) : ''}
        `;
    }

    renderRemoveButton(index) {
        return html`
            <div class="form-field__remove" @click=${evt => this.removeItem(evt, index)}>
                ${this.removeLabel}
            </div>
        `;
    }

    renderItems(fieldValue) {
        return fieldValue && fieldValue.value
            ? [...fieldValue.value].map((_, index) => this.renderFieldData(index))
            : '';
    }

    firstUpdated() {
        setTimeout(this.onComponentReady, 0);
    }

    onComponentReady = () => {
        const slot = this.querySelector('v-slot');
        this.slotContent = slot
            .assignedNodes()
            .map(node => node.cloneNode(true))
            .map(node => (node.outerHTML ? node.outerHTML : node.textContent) || '')
            .join('\n');
    };

    renderAddButton() {
        return html`
            <div class="form-field__add" @click=${this.addItem}>
                ${this.addLabel}
            </div>
        `;
    }

    render() {
        const canRender =
            this.maxItems <= 0 ||
            (this.fieldValue &&
                this.fieldValue.value &&
                this.fieldValue.value.length < this.maxItems);

        return html`
            ${this.renderItems(this.fieldValue)} ${canRender ? this.renderAddButton() : ''}
            <div class="item--hidden ${!this.slotContent ? 'd-contents' : 'd-none'}">
                <v-slot></v-slot>
            </div>
        `;
    }
}

export class NestedFormTogglerField extends FormToggler {
    firstUpdated() {
        this.content = this.querySelector('.toggler__content');
        this.getContent().addEventListener('transitionend', this.handleTransitionEnd);
    }

    disconnectedCallback() {
        if (this.content) {
            this.content.removeEventListener('transitionend', this.handleTransitionEnd);
        }
    }

    handleTransitionEnd = () =>
        animationHelpers.transitionEndedOnHeightHandler(this.getContent(), () =>
            this.changeValue(),
        );

    handleChange() {
        this.toggleStatus();
        if (!this.fieldValue) return; // to be investigated

        if (this.showContent()) {
            this.dispatchEvent(
                new CustomEvent('ADD_FIELD_ITEM', {
                    detail: { storeName: this.storeName, name: this.name },
                }),
            );
        }

        animationHelpers.handleHeightForAnimation(this.getContent());
    }
}

const mapStateToProps = (state, ctx) => {
    const selectors = cacheSelector(ctx, getSelectors);
    const fieldValue = selectors.getDataFieldValue(state, ctx.name);
    return {
        checked: ctx.inverted ? !fieldValue : !!fieldValue,
        disabled:
            checkoutSelectors.getShippingBlocked(state) ||
            selectors.isDataFieldDisabled(state, ctx.name),
    };
};

export default () => {
    customElements.define('e2-form-multifield', FormMultifield);
    customElements.define(
        'e2-nested-form-toggler',
        connect(mapStateToProps)(NestedFormTogglerField),
    );
};
